import { Box, Typography, styled } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';

import DataLabDataGrid from 'views/playground/features/data-lab/components/data-lab-data-grid';
import ChartCard from 'components/charts/chart-card';
import ChartEmptyState from 'components/charts/chart-empty-state';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import CategoryBarChart from 'views/playground/features/data-lab/components/charts/category-bar-chart';
import DistributionDonutChart from 'views/playground/features/data-lab/components/charts/distribution-donut-chart';
import DataLab from 'views/playground/features/data-lab/components/data-lab';
import KeyboardOptionList from 'views/playground/features/data-lab/components/keyboard-option-list';
import { MOTION_TOKENS } from 'views/playground/features/data-lab/constants/motion-tokens';
import { DrillPathEntry, useDrillPath } from 'views/playground/features/data-lab/hooks/use-drill-path';
import {
  FishGroupCageRow,
  deriveCagesInSite,
  deriveDistributionDonutData,
  deriveFishGroupsInCage,
} from 'views/playground/features/data-lab/utils/derive-chart-data';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
}));

const PageTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: chartPalette.text.primary,
}));

const BreadcrumbRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const Crumb = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isCurrent' && prop !== 'chartPalette',
})<{ isCurrent: boolean; chartPalette: ChartPalette }>(({ theme, isCurrent, chartPalette }) => ({
  font: 'inherit',
  fontSize: 20,
  fontWeight: 700,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: isCurrent ? chartPalette.text.primary : chartPalette.text.secondary,

  '&:hover': {
    color: chartPalette.text.primary,
  },

  '&:focus-visible': {
    outline: `2px solid ${chartPalette.series.categorical[0]}`,
    outlineOffset: 2,
  },
}));

const BackButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ theme, chartPalette }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  color: chartPalette.text.secondary,

  '&:hover': {
    color: chartPalette.text.primary,
  },

  '&:focus-visible': {
    outline: `2px solid ${chartPalette.series.categorical[0]}`,
    outlineOffset: 2,
  },
}));

const drillFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Keying on the current path replays the transition when drilling in or out.
// Charts skip their entrance animation so the two effects do not overlap. This
// wrapper also owns spacing between the chart and its sibling controls.
const LevelContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  animation: `${drillFade} ${MOTION_TOKENS.drill.duration}ms ${MOTION_TOKENS.drill.easing}`,
}));

// The page owns a typed path through sites, cages, and fish groups.
export const DrillDownScreenBody: React.FC = () => {
  const { formatKg } = useMetricFormatters();
  const chartPalette = useChartPalette();
  const { path, push, popTo, back, reset } = useDrillPath();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') back();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [back]);

  // Reuse the Overview's site groups, including its "Other" bucket.
  const siteDonutData = useMemo(() => deriveDistributionDonutData('Other'), []);

  const siteEntry: DrillPathEntry | undefined = path[0];
  const cageEntry: DrillPathEntry | undefined = path[1];

  const cagesData = useMemo(() => (siteEntry ? deriveCagesInSite(siteEntry.id) : { categories: [], series: [] }), [siteEntry]);
  const fishGroupRows = useMemo(() => (cageEntry ? deriveFishGroupsInCage(cageEntry.id) : []), [cageEntry]);

  const fishGroupColumns: GridColDef<FishGroupCageRow>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Fish group', flex: 1 },
      {
        field: 'biomassKg',
        headerName: 'Current biomass',
        flex: 1,
        renderCell: (params) => formatKg(params.row.biomassKg),
      },
      {
        field: 'stockedWeek',
        headerName: 'Stocked week',
        flex: 1,
        renderCell: (params) => `Week ${params.row.stockedWeek}`,
      },
    ],
    [formatKg]
  );

  const crumbLabels = ['All sites', ...path.map((entry) => entry.label)];

  const breadcrumb = (
    <BreadcrumbRow>
      {crumbLabels.map((label, index) => (
        <React.Fragment key={`${label}-${index}`}>
          {index > 0 && <ChevronRight size={16} />}
          <Crumb
            type="button"
            chartPalette={chartPalette}
            isCurrent={index === crumbLabels.length - 1}
            onClick={() => (index === 0 ? reset() : popTo(index - 1))}
          >
            {label}
          </Crumb>
        </React.Fragment>
      ))}
    </BreadcrumbRow>
  );

  const actions = path.length > 0 && (
    <BackButton type="button" chartPalette={chartPalette} onClick={back} aria-label="Back">
      <ArrowLeft size={16} />
    </BackButton>
  );

  const subtitle =
    path.length === 0
      ? 'Biomass by site · click a slice to drill into cages'
      : path.length === 1
        ? `Cages in ${siteEntry?.label ?? ''} · click a bar to drill into fish groups`
        : `Fish groups in ${cageEntry?.label ?? ''}`;

  const isCagesLevelEmpty = path.length === 1 && cagesData.categories.length === 0;
  // Use the same empty message for the cage chart and fish-group table instead
  // of showing the grid's default "No rows" overlay.
  const isFishGroupsLevelEmpty = path.length === 2 && fishGroupRows.length === 0;
  const isEmpty = isCagesLevelEmpty || isFishGroupsLevelEmpty;
  const emptyMessage = isFishGroupsLevelEmpty ? 'No fish groups for this selection.' : 'No cages for this selection.';

  // Chart marks are mouse-driven, so the option lists provide a keyboard path
  // for forward navigation.
  const drillIntoSite = (id: string | null) => {
    if (!id) return;
    const category = siteDonutData.find((point) => point.id === id);
    push({ level: 'site', id, label: category?.label ?? id });
  };

  const drillIntoCage = (id: string | null) => {
    if (!id) return;
    const category = cagesData.categories.find((candidate) => candidate.id === id);
    push({ level: 'cage', id, label: category?.label ?? id });
  };

  const renderLevel = () => {
    if (path.length === 0) {
      return (
        <>
          <DistributionDonutChart data={siteDonutData} skipAnimation onSelect={drillIntoSite} />
          <KeyboardOptionList
            options={siteDonutData.map((point) => ({ id: point.id, label: point.label }))}
            selectedId={null}
            onSelect={drillIntoSite}
            ariaLabel="Drill into a site"
            toggleable={false}
          />
        </>
      );
    }

    if (path.length === 1) {
      return (
        <>
          <CategoryBarChart data={cagesData} skipAnimation onSelect={drillIntoCage} />
          {cagesData.categories.length > 0 && (
            <KeyboardOptionList
              options={cagesData.categories.map((category) => ({ id: category.id, label: category.label }))}
              selectedId={null}
              onSelect={drillIntoCage}
              ariaLabel="Drill into a cage"
              toggleable={false}
            />
          )}
        </>
      );
    }

    return (
      <DataLabDataGrid
        chartPalette={chartPalette}
        autoHeight
        hideFooter
        rows={fishGroupRows}
        columns={fishGroupColumns}
        // Assign parity classes for the scoped grid row styling.
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
      />
    );
  };

  return (
    <>
      <HeaderRow>
        <PageTitle chartPalette={chartPalette} variant="h2">
          Drill-down
        </PageTitle>
      </HeaderRow>

      <ChartCard
        title={breadcrumb}
        subtitle={subtitle}
        actions={actions}
        isEmpty={isEmpty}
        emptyState={<ChartEmptyState height={320} message={emptyMessage} clearLabel="Clear filter" onClear={back} />}
      >
        <LevelContent key={path.map((entry) => entry.id).join('/')}>{renderLevel()}</LevelContent>
      </ChartCard>
    </>
  );
};

const DrillDownScreen: React.FC = () => (
  <DataLab>
    <DrillDownScreenBody />
  </DataLab>
);

export default DrillDownScreen;
